import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { X } from "@phosphor-icons/react";

/**
 * Autocomplete over the user's people directory.
 *
 * Modes:
 *   - "single": free-text single-name input (leader, applicant)
 *   - "multi":  chip-style multi-name input (participants, team)
 *
 * The dropdown suggests matching existing people by name. Typing an unknown
 * name is still allowed — pressing Enter / comma commits it (multi) or just
 * uses whatever is typed (single). Selecting a suggestion inserts the name.
 */
export default function PeopleAutocomplete({ mode = "single", value, onChange, placeholder, testid }) {
  const [people, setPeople] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const rootRef = useRef();
  const inputRef = useRef();

  const isMulti = mode === "multi";
  const names = useMemo(() => (isMulti ? (value || []) : []), [isMulti, value]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/people");
      setPeople(data);
    } catch { /* keep last known set */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onDocClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const chosen = new Set(names.map((n) => n.toLowerCase()));
    return people
      .filter((p) => p.name.toLowerCase().includes(q) && !chosen.has(p.name.toLowerCase()))
      .slice(0, 6);
  }, [query, people, names]);

  const commit = (raw) => {
    const name = (raw ?? query).trim();
    if (!name) return;
    if (isMulti) {
      if (names.some((n) => n.toLowerCase() === name.toLowerCase())) { setQuery(""); return; }
      onChange([...names, name]);
      setQuery("");
    } else {
      onChange(name);
      setQuery("");
      setOpen(false);
    }
  };

  const removeName = (name) => {
    if (!isMulti) return;
    onChange(names.filter((n) => n !== name));
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, suggestions.length - 1)); setOpen(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" || (isMulti && e.key === ",")) {
      if (open && suggestions[focusIdx]) { e.preventDefault(); commit(suggestions[focusIdx].name); }
      else if (isMulti && query.trim()) { e.preventDefault(); commit(); }
    } else if (e.key === "Backspace" && isMulti && !query && names.length) {
      onChange(names.slice(0, -1));
    } else if (e.key === "Escape") { setOpen(false); }
  };

  // Single-mode initial value: hydrate from `value` prop
  useEffect(() => { if (!isMulti && typeof value === "string" && value !== query && !open) setQuery(value); /* eslint-disable-next-line */ }, [value, isMulti]);

  const inp = "flex-1 min-w-[8ch] outline-none bg-transparent text-sm py-1";

  return (
    <div ref={rootRef} className="relative">
      <div className={`w-full rounded-xl border border-slate-200 px-3 py-2 focus-within:ring-2 focus-within:ring-teal-400 flex flex-wrap gap-1 items-center ${!isMulti ? "" : "min-h-[42px]"}`}>
        {isMulti && names.map((n) => (
          <span key={n} data-testid={`${testid || "person"}-chip-${n}`} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
            {n}
            <button type="button" onClick={() => removeName(n)} className="text-slate-400 hover:text-red-500 p-0.5"><X size={12} weight="bold"/></button>
          </span>
        ))}
        <input
          ref={inputRef}
          data-testid={testid || "person-input"}
          value={query}
          placeholder={placeholder || (isMulti ? "Add a name…" : "Type a name…")}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setFocusIdx(0); if (!isMulti) onChange(e.target.value); }}
          onBlur={() => { if (!isMulti) onChange(query.trim()); }}
          onKeyDown={onKeyDown}
          className={inp}
          autoComplete="off"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div role="listbox" data-testid={`${testid || "person"}-dropdown`} className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-20 py-1 max-h-56 overflow-y-auto">
          {suggestions.map((p, i) => (
            <button
              type="button"
              key={p.id}
              onMouseDown={(e) => { e.preventDefault(); commit(p.name); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === focusIdx ? "bg-slate-100" : "hover:bg-slate-50"}`}
            >
              <span className="w-6 h-6 rounded-full al-gradient text-white flex items-center justify-center text-[10px] font-semibold">{p.name.slice(0,1).toUpperCase()}</span>
              <span className="flex-1 min-w-0">
                <span className="font-medium truncate block">{p.name}</span>
                {p.role && <span className="text-xs text-slate-500 truncate block">{p.role}</span>}
              </span>
              <span className="text-[10px] text-slate-400">{p.reference_count}×</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
